import { useEffect, useState } from 'react'
import { checkSessionStatus } from '../services/stripeService'
import { getCurrentUser, setPremiumStatus, checkPremiumStatus } from '../services/firebaseService'
import '../styles/pages/success.css'

function Success() {
  const [loading, setLoading] = useState(true)
  const [sessionStatus, setSessionStatus] = useState(null)
  
  // Get session_id from URL
  const urlParams = new URLSearchParams(window.location.search)
  const sessionId = urlParams.get('session_id')

  useEffect(() => {
    if (sessionId) {
      checkSessionStatus(sessionId)
        .then(session => {
          setSessionStatus(session)
          setLoading(false)
        })
        .catch(error => {
          console.error('Error checking session:', error)
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [sessionId])

  if (loading) {
    return (
      <div className="success-page">
        <div className="success-content">
          <h2>⏳ Verificerer betaling...</h2>
        </div>
      </div>
    )
  }

  // Get user_id - prioritize logged-in user, then URL, then sessionStorage
  const currentUser = getCurrentUser()
  let userId = currentUser?.uid
  
  if (!userId) {
    // Fallback: get from URL or sessionStorage
    userId = urlParams.get('user_id') || sessionStorage.getItem('parkort_payment_user_id')
  }
  
  // Get plan from URL or sessionStorage (backup)
  let plan = urlParams.get('plan') // 'monthly' or 'yearly'
  if (!plan) {
    plan = sessionStorage.getItem('parkort_payment_plan') || 'monthly'
  }
  
  // Get topicId from URL or sessionStorage (backup)
  let topicId = urlParams.get('topic_id')
  if (!topicId) {
    topicId = sessionStorage.getItem('parkort_payment_topic_id')
  }
  
  const isSuccess = sessionStatus?.status === 'paid' || urlParams.get('payment_id') || urlParams.get('success')

  // Save premium status to Firebase if payment successful
  useEffect(() => {
    if (isSuccess && userId) {
      // Calculate expiry date based on plan
      const expiryDate = new Date()
      if (plan === 'yearly') {
        expiryDate.setFullYear(expiryDate.getFullYear() + 1)
      } else {
        expiryDate.setMonth(expiryDate.getMonth() + 1)
      }

      // Update Firebase with premium status - with retry logic
      const updatePremiumWithRetry = async (retryCount = 0) => {
        const maxRetries = 3
        
        try {
          // Update premium status
          const updateResult = await setPremiumStatus(userId, true, expiryDate)
          if (!updateResult) {
            throw new Error('setPremiumStatus returned false')
          }
          
          // Wait a moment for Firebase to propagate
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // Verify the update was successful
          let verified = false
          for (let i = 0; i < 5; i++) {
            verified = await checkPremiumStatus(userId)
            if (verified) break
            // Wait before retry
            await new Promise(resolve => setTimeout(resolve, 500))
          }
          
          if (!verified && retryCount < maxRetries) {
            return updatePremiumWithRetry(retryCount + 1)
          }
          
          if (!verified) {
            alert('Betaling gennemført, men der var et problem med at verificere premium status. Prøv at opdatere siden eller kontakt support.')
          }
          
          // Redirect back to preferences page with topicId (if available)
          // Wait a moment to show success message, then redirect
          setTimeout(() => {
            const basePath = window.location.pathname.includes('/dist/') ? '/dist' : ''
            const redirectParams = new URLSearchParams({
              success: 'true'
            })
            if (topicId) {
              redirectParams.set('topic_id', topicId)
            }
            window.location.href = `${basePath}/?${redirectParams.toString()}`
          }, 2000) // Show success message for 2 seconds
          
        } catch (error) {
          console.error(`❌ Error saving premium status (forsøg ${retryCount + 1}):`, error)
          
          if (retryCount < maxRetries) {
            // Retry after a delay
            setTimeout(() => {
              updatePremiumWithRetry(retryCount + 1)
            }, 2000)
          } else {
            // Max retries reached - show error but still redirect
            alert('Betaling gennemført, men der var et problem med at opdatere premium status. Kontakt support hvis du ikke får adgang.')
            
            setTimeout(() => {
              const basePath = window.location.pathname.includes('/dist/') ? '/dist' : ''
              const redirectParams = new URLSearchParams({
                success: 'true'
              })
              if (topicId) {
                redirectParams.set('topic_id', topicId)
              }
              window.location.href = `${basePath}/?${redirectParams.toString()}`
            }, 2000)
          }
        }
      }
      
      // Start the update process
      updatePremiumWithRetry()
      
    } else if (isSuccess) {
      // Fallback: Also save to localStorage if no userId (shouldn't happen)
      localStorage.setItem('parkort_premium', 'true')
      localStorage.setItem('parkort_premium_date', new Date().toISOString())
      // Premium access granted (localStorage fallback)
      
      // Redirect back to preferences page
      setTimeout(() => {
        const basePath = window.location.pathname.includes('/dist/') ? '/dist' : ''
        const redirectParams = new URLSearchParams({
          success: 'true'
        })
        if (topicId) {
          redirectParams.set('topic_id', topicId)
        }
        window.location.href = `${basePath}/?${redirectParams.toString()}`
      }, 2000)
    }
  }, [isSuccess, userId, plan, topicId])

  return (
    <div className="success-page">
      <div className="success-content">
        {isSuccess ? (
          <>
            <div className="success-icon">✅</div>
            <h2>Tak for din betaling! 🎉</h2>
            <p style={{ fontSize: '1.1em', fontWeight: '600', color: 'var(--cyber-blue)', marginTop: '12px' }}>
              Du er nu Premium bruger!
            </p>
            <p>Du har nu adgang til alle premium moduler.</p>
            <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginTop: '12px' }}>
              {topicId 
                ? 'Du bliver automatisk ført tilbage til modulet du prøvede at åbne...'
                : 'Du bliver automatisk ført tilbage til appen...'}
            </p>
            <div style={{ 
              marginTop: '24px', 
              padding: '16px', 
              background: 'rgba(81, 207, 102, 0.1)', 
              borderRadius: '12px',
              border: '1px solid rgba(81, 207, 102, 0.3)'
            }}>
              <p style={{ margin: 0, fontSize: '0.9em' }}>
                ✅ Logget ind som Premium<br/>
                ✅ Alle Premium moduler tilgængelige<br/>
                ✅ Klar til at prøve alle kort!
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="success-icon">⚠️</div>
            <h2>Betaling ikke gennemført</h2>
            <p>Din betaling blev ikke gennemført. Prøv igen hvis du vil fortsætte.</p>
            <button className="btn" onClick={() => {
              window.location.href = '/'
            }}>
              Tilbage til appen
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default Success
